/* =====================================================================
 * AccountAvatar — 로그인 계정 아바타 (사이드바 계정 블록 / 내 스킬 카드 공용)
 *
 * 사이드바 하단 계정 블록에 앉는 아바타가 원형이다. 프로필 사진이 있으면
 * 사진, 없으면(또는 사진 로드에 실패하면) 이름/이메일 첫 글자를 한 단계
 * 들어올린(raised) 회색 squircle 위에 올린다. "내 스킬" 카드처럼 "이 계정이
 * 만든 것" 을 표시하는 자리도 같은 컴포넌트를 써서, 두 표면이 조용히
 * 어긋나지 않게 한다.
 * ===================================================================== */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { avatarInitial } from '@auw/workspace-utils';

/* 한 단계 들어올린 타일 표면. 사이드바의 회색 surface 토큰(SIDEBAR_GRAY_SURFACE)
 * 중 raised 계열과 같은 값이며 사이드바가 이 토큰을 참조한다. */
export const ACCOUNT_AVATAR_SURFACE = {
  light: {
    bg: '#ffffff',
    shadow: '0 1px 6px rgba(0,0,0,0.12), inset 0 0 0 1px rgba(255,255,255,0.35)',
    ink: '#1f2937',
  },
  dark: {
    bg: 'linear-gradient(145deg, #4b4b4f, #3a3a3d)',
    shadow: '0 1px 5px rgba(0,0,0,0.28), inset 0 0 0 1px rgba(255,255,255,0.06)',
    ink: '#ececef',
  },
} as const;

export interface AccountAvatarProps {
  name?: string | null;
  email?: string | null;
  picture?: string | null;
  /** 한 변 길이(px). 사이드바 계정 블록은 32. */
  size?: number;
  className?: string;
}

export const AccountAvatar: React.FC<AccountAvatarProps> = ({
  name,
  email,
  picture,
  size = 32,
  className,
}) => {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [picture]);

  /* 이름·이메일이 모두 없는 게스트 미리보기는 사이드바가 쓰던 'U' 를 유지한다
   * (`avatarInitial` 의 기본값 '?' 는 아바타 자리에선 깨진 것처럼 보인다). */
  const initial = !name && !email ? 'U' : avatarInitial(name, email);

  return (
    <Circle $size={size} className={className} data-account-avatar="true">
      {picture && !imgError ? (
        <Image
          src={picture}
          alt="profile"
          referrerPolicy="no-referrer"
          onError={() => setImgError(true)}
        />
      ) : (
        initial
      )}
    </Circle>
  );
};

const Circle = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  border-radius: 22%;
  background: ${({ theme }) => ACCOUNT_AVATAR_SURFACE[theme.mode].bg};
  box-shadow: ${({ theme }) => ACCOUNT_AVATAR_SURFACE[theme.mode].shadow};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => ACCOUNT_AVATAR_SURFACE[theme.mode].ink};
  font-weight: 700;
  font-size: ${({ $size }) => Math.round($size * 0.42)}px;
  line-height: 1;
  user-select: none;
  flex-shrink: 0;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  filter: ${({ theme }) =>
    theme.mode === 'dark' ? 'brightness(0.9) saturate(0.9)' : 'none'};
`;
